import React, { useState, useEffect, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { FaCheckCircle } from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import './PathComponent.css';

function parsePathColorHex(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(s)) return null;
  if (s.length === 4) {
    return `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`.toLowerCase();
  }
  return s.toLowerCase();
}

function contrastTextForHexBg(hex) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 156 ? '#212529' : '#ffffff';
}

/** Match record value to stage (key is API value_key; value is label). */
function stageMatchesCurrent(stage, currentValue) {
  if (currentValue == null || currentValue === '') {
    return false;
  }
  const cv = String(currentValue);
  const k = stage?.key != null ? String(stage.key) : '';
  const v = stage?.value != null ? String(stage.value) : '';
  return (k !== '' && k === cv) || (v !== '' && v === cv);
}

/**
 * Hide path stages the user's role cannot use (role_selectable === false),
 * but always keep the stage matching the record's current value.
 */
function visiblePathPicklistValues(picklistValues, currentValue) {
  if (!picklistValues || picklistValues.length === 0) {
    return [];
  }
  return picklistValues.filter(
    stage =>
      stage?.role_selectable !== false ||
      stageMatchesCurrent(stage, currentValue)
  );
}

function normalizePicklistKey(v) {
  if (v == null || v === '') {
    return '';
  }
  return String(v).trim();
}

function stagePicklistKey(stage) {
  const k = normalizePicklistKey(stage?.key);
  if (k) {
    return k;
  }
  return normalizePicklistKey(stage?.value);
}

/** Scalar from object/list row cell (may be wrapped as { value }). */
function extractListCellScalar(cell) {
  if (cell == null || cell === '') {
    return '';
  }
  if (typeof cell === 'object' && cell !== null && 'value' in cell) {
    return normalizePicklistKey(cell.value);
  }
  return normalizePicklistKey(cell);
}

function isTruthyFlag(v) {
  if (v === true || v === 1) return true;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === 'true' || s === '1' || s === 'yes';
  }
  return false;
}

/**
 * PathComponent — visual stage progression for path picklists (chevrons, current/complete states).
 *
 * @param {Object} field - The field configuration with is_path_field = true
 * @param {String} currentValue - The current value of the picklist field
 * @param {Function} onStageChange - Callback when user changes the stage
 * @param {Array} picklistValues - Array of picklist values/stages
 * @param {Boolean} isUpdating - Whether an update is in progress
 * @param {string} [pathHistoryParentObject] - Base object name (e.g. opportunity); fetches {name}_history
 * @param {string} [pathHistoryRecordId] - Parent record id for history rows (parent_id)
 * @param {string} [pathHistoryFieldName] - Tracked field API name (field_name on history rows)
 */
const PathComponent = ({
  field,
  readOnly: readOnlyProp = undefined,
  currentValue,
  onStageChange,
  picklistValues = [],
  isUpdating = false,
  pathHistoryParentObject = null,
  pathHistoryRecordId = null,
  pathHistoryFieldName = null,
}) => {
  const [selectedStage, setSelectedStage] = useState(null);
  const [currentStageIndex, setCurrentStageIndex] = useState(-1);
  /** null = skip or error; Set = loaded (may be empty) */
  const [pathHistoryVisitedKeys, setPathHistoryVisitedKeys] = useState(null);

  const readOnly = useMemo(() => {
    if (readOnlyProp != null) return Boolean(readOnlyProp);
    if (!field) return false;
    return (
      isTruthyFlag(field.is_readonly) ||
      isTruthyFlag(field.isReadonly) ||
      field.readonly === true
    );
  }, [field, readOnlyProp]);

  const stagesToRender = useMemo(
    () => visiblePathPicklistValues(picklistValues, currentValue),
    [picklistValues, currentValue]
  );

  const effectiveVisitedKeys = useMemo(() => {
    if (pathHistoryVisitedKeys === null) {
      return null;
    }
    const s = new Set(pathHistoryVisitedKeys);
    const cur = normalizePicklistKey(currentValue);
    if (cur) {
      s.add(cur);
    }
    return s;
  }, [pathHistoryVisitedKeys, currentValue]);

  useEffect(() => {
    const parent = pathHistoryParentObject != null ? String(pathHistoryParentObject).trim() : '';
    const rid = pathHistoryRecordId != null ? String(pathHistoryRecordId).trim() : '';
    const fname = pathHistoryFieldName != null ? String(pathHistoryFieldName).trim() : '';

    if (!parent || !rid || !fname || parent.toLowerCase().endsWith('_history')) {
      setPathHistoryVisitedKeys(null);
      return;
    }

    const historyObject = `${parent}_history`;
    const ac = new AbortController();
    let cancelled = false;

    (async () => {
      try {
        const qp = new URLSearchParams();
        qp.append('limit', '0');
        qp.append('offset', '0');
        qp.append('fields', 'old_value,new_value,field_name');
        qp.append('filter1', `parent_id:equals:${rid}`);
        qp.append('filter2', `field_name:equals:${fname}`);
        qp.append('logic', 'AND');

        const api = new DataApi(`object/list/${historyObject}`);
        const res = await api.fetchAll(qp.toString(), { signal: ac.signal });
        if (cancelled) {
          return;
        }
        if (!res.data?.success) {
          setPathHistoryVisitedKeys(null);
          return;
        }
        const rows = res.data.data || [];
        const keys = new Set();
        for (let i = 0; i < rows.length; i += 1) {
          const row = rows[i];
          const ov = extractListCellScalar(row.old_value);
          const nv = extractListCellScalar(row.new_value);
          if (ov) {
            keys.add(ov);
          }
          if (nv) {
            keys.add(nv);
          }
        }
        setPathHistoryVisitedKeys(keys);
      } catch (e) {
        if (cancelled || e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') {
          return;
        }
        setPathHistoryVisitedKeys(null);
      }
    })();

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [pathHistoryParentObject, pathHistoryRecordId, pathHistoryFieldName, currentValue]);

  useEffect(() => {
    const currentIndex = stagesToRender.findIndex(pv =>
      stageMatchesCurrent(pv, currentValue)
    );
    setCurrentStageIndex(currentIndex);
  }, [currentValue, stagesToRender]);

  const isStageSelectable = stage =>
    stage && stage.role_selectable !== false;

  const handleStageClick = (stage, index) => {
    // Don't allow selection during update
    if (isUpdating || readOnly) {
      return;
    }
    if (!isStageSelectable(stage)) {
      return;
    }
    setSelectedStage(stage);
  };

  const handleUpdate = () => {
    if (
      !readOnly &&
      selectedStage &&
      isStageSelectable(selectedStage) &&
      onStageChange &&
      !isUpdating
    ) {
      onStageChange(selectedStage.key || selectedStage.value);
      setSelectedStage(null);
    }
  };

  const handleCancel = () => {
    if (!isUpdating && !readOnly) {
      setSelectedStage(null);
    }
  };

  // Reset selected stage when update completes
  useEffect(() => {
    if (!isUpdating) {
      setSelectedStage(null);
    }
  }, [isUpdating]);

  useEffect(() => {
    if (readOnly) {
      setSelectedStage(null);
    }
  }, [readOnly]);

  if (!stagesToRender || stagesToRender.length === 0) {
    return null;
  }

  return (
    <div className={`path-component-container ${isUpdating ? 'updating' : ''} ${readOnly ? 'readonly' : ''}`}>
      <div className="path-component">
        <div className="path-stages">
          {stagesToRender.map((stage, index) => {
            const isActive = index === currentStageIndex;
            const isSelected = selectedStage?.key === stage.key || selectedStage?.value === stage.value;
            const stageKey = stagePicklistKey(stage);
            const historyAware =
              effectiveVisitedKeys != null && currentStageIndex >= 0 && stageKey !== '';

            let displayCompleted;
            let displayFuture;
            let isHistoryGap;

            if (historyAware) {
              const visited = effectiveVisitedKeys.has(stageKey);
              if (isActive) {
                displayCompleted = false;
                displayFuture = false;
                isHistoryGap = false;
              } else if (index < currentStageIndex) {
                if (visited) {
                  // Past current position and in history: completed + checkmark
                  displayCompleted = true;
                  displayFuture = false;
                  isHistoryGap = false;
                } else {
                  // Skipped gap (e.g. went stage 1 → 3, stage 2 never in history)
                  displayCompleted = false;
                  displayFuture = false;
                  isHistoryGap = true;
                }
              } else {
                // index > current: always normal upcoming (grey), even if visited before — avoids blue “completed” ahead of current
                displayCompleted = false;
                displayFuture = true;
                isHistoryGap = false;
              }
            } else {
              displayCompleted = index < currentStageIndex;
              displayFuture = index > currentStageIndex;
              isHistoryGap = false;
            }

            const selectable = isStageSelectable(stage);
            const cursor =
              isUpdating || readOnly || !selectable ? 'not-allowed' : 'pointer';
            const customHex = selectable
              ? parsePathColorHex(stage.path_color || stage.pathColor)
              : null;
            const customFg = customHex ? contrastTextForHexBg(customHex) : null;
            /** Per-value path_color for current + completed only; selected (pending Update) uses one global color via CSS. */
            const applyCustomPathColor =
              Boolean(customHex) &&
              selectable &&
              (displayCompleted || isActive) &&
              !isSelected;

            const titleParts = [`${stage.label || stage.value}`];
            if (isActive) titleParts.push('(Current)');
            if (displayCompleted) titleParts.push('(Completed)');
            if (isSelected) titleParts.push('(Selected)');
            if (readOnly) titleParts.push('(Read only)');
            if (!selectable) titleParts.push('(Not available for your role)');
            if (isHistoryGap) titleParts.push('(Skipped)');
            if (historyAware && displayFuture) {
              titleParts.push('(Upcoming)');
            }

            return (
              <div
                key={stage.key || stage.value || index}
                className={`path-stage ${isActive ? 'active' : ''} ${isSelected ? 'selected' : ''} ${displayCompleted ? 'completed' : ''} ${displayFuture ? 'future' : ''} ${!selectable ? 'path-stage-not-selectable' : ''} ${applyCustomPathColor ? 'path-stage-custom-color' : ''} ${isHistoryGap ? 'path-stage-history-gap' : ''}`}
                onClick={() => handleStageClick(stage, index)}
                title={titleParts.join(' ')}
                style={{
                  cursor,
                  pointerEvents: isUpdating || readOnly ? 'none' : 'auto',
                  ...(applyCustomPathColor
                    ? {
                      '--path-custom-bg': customHex,
                      '--path-custom-fg': customFg,
                    }
                    : {}),
                }}
              >
                <div className="stage-content">

                  <span className="stage-label mx-2">
                    {stage.label || stage.value}
                  </span>
                  {displayCompleted && !isSelected && !isActive && (
                    <FaCheckCircle className="stage-icon completed-icon" style={{ marginRight: '6px', fontSize: '12px' }} />
                  )}
                </div>
                {index < stagesToRender.length - 1 && (
                  <div className="stage-arrow"></div>
                )}
              </div>
            );
          })}
        </div>

        {selectedStage && !isUpdating && !readOnly && (
          <div className="path-actions path-actions-update">
            <Button
              variant="primary"
              size="sm"
              onClick={handleUpdate}
              disabled={isUpdating}
              className="mark-stage-btn"
            >
              Update
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleCancel}
              disabled={isUpdating}
              className="mark-stage-btn"
            >
              Cancel
            </Button>
          </div>
        )}

        {isUpdating && (
          <div className="path-actions">
            <Button
              variant="primary"
              size="sm"
              disabled={true}
              className="mark-stage-btn"
            >
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Updating...
            </Button>
          </div>
        )}
      </div>

      {/*field?.field_label && (
        <div className="path-field-info">
          <small className="text-muted">
            Path: {field.field_label}
          </small>
        </div>
      )*/}
    </div>
  );
};

export default PathComponent;
